<template>
  <div>
    <osv-app-alert />

    <form
      @submit.prevent="applyForJob"
      v-if="!applySend"
      enctype="multipart/form-data"
    >
      <!-- Title -->
      <div class="columns">
        <div class="column">
          <label>
            {{ $t("title") }}
          </label>
        </div>
        <div class="column">
          <b-radio v-model="title" checked name="title" native-value="">
            {{ $t("titleList.undefined") }}
          </b-radio>
        </div>
        <div class="column">
          <b-radio v-model="title" name="title" native-value="mrs">
            {{ $t("titleList.mrs") }}
          </b-radio>
        </div>
        <div class="column">
          <b-radio v-model="title" name="title" native-value="mr">
            {{ $t("titleList.mr") }}
          </b-radio>
        </div>
      </div>

      <!-- Lastname & Firstname -->
      <div class="columns mb-0">
        <div class="column pb-0">
          <b-field
            :label="$t('lastname')"
            custom-class="required"
            label-position="on-border"
          >
            <b-input required v-model="lastname" />
          </b-field>
        </div>
        <div class="column pb-0">
          <b-field
            :label="$t('firstname')"
            custom-class="required"
            label-position="on-border"
          >
            <b-input required v-model="firstname" />
          </b-field>
        </div>
      </div>

      <!-- Email -->
      <div class="form-group">
        <b-field
          :label="$t('email')"
          custom-class="required"
          label-position="on-border"
        >
          <b-input required v-model="email" type="email" />
        </b-field>
      </div>

      <!-- Phone -->
      <div class="form-group">
        <b-field
          :label="$t('phone')"
          custom-class="required"
          label-position="on-border"
        >
          <b-input
            required
            v-model="phone"
            type="tel"
            v-cleave="masks.frenchPhone"
            @input.native="onPhoneNumberInput"
            @keydown.native="phoneNumberKeyDown"
            placeholder="06-00-00-00-00"
            maxlength="14"
            :has-counter="false"
          />
        </b-field>
      </div>

      <!-- Tenants list -->
      <div v-if="post.tenants.length > 1" class="tenants-list">
        <p class="tenants-label">
          <small>{{ $t("whereDoYouWantToApply") }}</small>
        </p>
        <div class="columns is-multiline">
          <div
            class="column is-half"
            v-for="tenant in post.tenants"
            :key="tenant.id"
          >
            <b-checkbox v-model="tenants" :native-value="tenant.id">
              {{ tenant.name }}
              <span class="text-grey">
                {{ tenant.zipcode }} {{ tenant.city }}
              </span>
            </b-checkbox>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="form-group">
        <b-field :label="$t('content')" label-position="on-border">
          <b-input v-model="content" type="textarea" />
        </b-field>
      </div>

      <!-- CV -->
      <div class="form-group">
        <b-field
          class="file is-primary"
          :class="{ 'has-name': !!curriculumVitae }"
        >
          <b-upload
            required
            v-model="curriculumVitae"
            accept="application/pdf"
            class="file-label"
            rounded
          >
            <span class="file-cta">
              <b-icon class="file-icon" icon="file-account" />
              <span class="file-label">
                {{ $t("dropCurriculumVitae") }}
              </span>
            </span>
            <span class="file-name" v-if="curriculumVitae">
              {{ curriculumVitae.name }}
            </span>
          </b-upload>
        </b-field>
      </div>

      <!-- Motivation letter -->
      <div class="form-group">
        <b-field
          class="file is-primary"
          :class="{ 'has-name': !!letterMotivation }"
        >
          <b-upload
            accept="application/pdf"
            v-model="letterMotivation"
            class="file-label"
            rounded
          >
            <span class="file-cta">
              <b-icon class="file-icon" icon="file-document" />
              <span class="file-label">
                {{ $t("dropMotivationLetter") }}
              </span>
            </span>
            <span class="file-name" v-if="letterMotivation">
              {{ letterMotivation.name }}
            </span>
          </b-upload>
        </b-field>
      </div>

      <!-- GDPR checkbox -->
      <div class="form-group">
        <b-checkbox v-model="acceptGdpr">
          {{ $t("acceptGdpr") }}
        </b-checkbox>
        <br />
        <a
          :href="osvConfig.paths.gdprCandidate"
          v-if="osvConfig.paths.gdprCandidate"
          target="_blank"
        >
          {{ $t("gdpr") }}
        </a>
      </div>

      <!-- Submit -->
      <div class="text-right form-group">
        <b-button
          outlined
          rounded
          type="is-primary"
          native-type="submit"
          class="loadable-button"
        >
          {{ $t("sendApply") }}
        </b-button>
      </div>
    </form>

    <!-- Success message -->
    <div v-if="applySend">
      <alert-message
        :messages="[$t('applySent', { firstname })]"
        type="success"
      />
    </div>
  </div>
</template>

<script>
import { LocaleMixin } from "@/mixins/LocaleMixin";
import { HelpersMixin } from "@/mixins/HelpersMixin";
import { ConfigMixin } from "@/mixins/ConfigMixin";
import { PhoneNumberMixin } from "@/mixins/PhoneNumberMixin";
import VueI18n from "vue-i18n";
import cug from "@/services/cug";
import OsvAppAlert from "@/components/shared/OsvAppAlert";
import AlertMessage from "@/components/shared/AlertMessage";

// Import specific Buefy components
import { Upload } from "buefy";
import Vue from "vue";
Vue.use(Upload);

export default {
  name: "RecruitmentPostForm",
  mixins: [LocaleMixin, HelpersMixin, ConfigMixin, PhoneNumberMixin],
  props: {
    post: Object
  },
  components: { AlertMessage, OsvAppAlert },

  data() {
    return {
      email: "",

      title: "",
      lastname: "",
      firstname: "",
      phone: "",
      content: "",
      tenants: [],
      curriculumVitae: null,
      letterMotivation: null,
      acceptGdpr: false,

      applySend: false
    };
  },

  methods: {
    applyForJob: function() {
      this.loadButton(true);

      if (!this.acceptGdpr) {
        this.$root.$emit(
          "OsvAlert_SimpleAlert",
          this.$t("youMustAcceptGdpr", { name: this.tenant })
        );
        this.loadButton(false);
        return false;
      }

      if (this.tenants.length === 0 && this.post.tenants.length > 1) {
        this.$root.$emit(
          "OsvAlert_SimpleAlert",
          this.$t("youMustAddTenant", { name: this.tenant })
        );
        this.loadButton(false);
        return false;
      }

      let formData = new FormData();
      if (this.letterMotivation) {
        formData.append("letter_of_motivation", this.letterMotivation);
      }
      formData.append("curriculum_vitae", this.curriculumVitae);
      formData.append("content", this.content);
      formData.append("post_id", this.post.id);
      formData.append("email", this.email);
      formData.append("phone", this.phone);
      formData.append("lastname", this.lastname);
      formData.append("firstname", this.firstname);

      if (this.post.tenants.length === 1) {
        formData.append("tenants", this.post.tenants[0].id);
      } else {
        formData.append("tenants", this.tenants.join(";"));
      }

      cug
        .applyForJob(formData)
        .then(() => {
          this.applySend = true;
        })
        .catch(err => {
          this.$root.$emit("OsvAlert_ErrorFromApi", err, "recruitment");
        })
        .finally(() => {
          this.loadButton(false);
        });
    }
  },

  // Translate component
  mounted() {
    this.$i18n.locale = this.getLocale();
  },
  i18n: new VueI18n({
    locale: "fr",
    messages: {
      en: {
        email: "Email",
        lastname: "Lastname",
        firstname: "Firstname",
        title: "Title :",
        phone: "Phone",
        content: "Message",
        curriculumVitae: "Curriculum vitae",
        dropCurriculumVitae: "Add your curriculum vitae (required, .pdf)",
        motivationLetter: "Cover letter",
        dropMotivationLetter: "Add your cover letter (.pdf)",
        whereDoYouWantToApply: "Where do you want to apply ?",
        titleList: {
          undefined: "No information",
          mr: "Mister",
          mrs: "Madame"
        },
        acceptGdpr:
          "I have read and agree to the processing of my personal data",
        gdpr: "Data Management Policy",
        youMustAcceptGdpr: "You must accept data management policy",
        youMustAddTenant: "You must choose at least one structure",
        sendApply: "Apply",
        applySent: "Thank you %{firstname}, Your application has been sent."
      },
      fr: {
        email: "Email",
        lastname: "Nom de famille",
        firstname: "Prénom",
        title: "Civilité :",
        phone: "Téléphone",
        content: "Message",
        curriculumVitae: "Curriculum vitae",
        dropCurriculumVitae: "Ajouter votre CV (requis, .pdf)",
        motivationLetter: "Lettre de motivation",
        dropMotivationLetter: "Ajouter votre lettre de motivation (.pdf)",
        whereDoYouWantToApply: "Où voulez-vous postuler ?",
        titleList: {
          undefined: "Non renseignée",
          mr: "Monsieur",
          mrs: "Madame"
        },
        acceptGdpr:
          "J'ai lu et j'accepte le traitement de mes données personnelles",
        gdpr: "Politique de gestion des données",
        youMustAcceptGdpr:
          "Vous devez accepter la politique de gestion des données",
        youMustAddTenant: "Vous devez choisir au moins une structure",
        sendApply: "Candidater",
        applySent: "Merci %{firstname}, votre candidature a bien été envoyée."
      }
    }
  })
};
</script>

<style lang="scss" src="../../assets/styles/buefy/base.scss"></style>
<style lang="scss">
.tenants-list {
  margin-bottom: 2em;
  margin-top: 2em;
  .tenants-label {
    color: #646464;
    margin-bottom: 0.8em;
    small {
      font-weight: 700;
      font-size: 0.75em;
    }
  }
}
</style>
